/**
 * SampleDataModal - Modal to display sample data from vendor file tables
 *
 * Displays a resizable table with sample data rows from a vendor file table.
 */

import React, { useMemo } from 'react'
import { Modal, Button, Table } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import type { VendorSampleData, TableFieldMapping } from './types'

export interface SampleDataModalProps {
  visible: boolean
  onClose: () => void
  tableId: string | null
  sampleData: VendorSampleData | null
  tableFieldMappings: TableFieldMapping[]
}

export const SampleDataModal: React.FC<SampleDataModalProps> = ({
  visible,
  onClose,
  tableId,
  sampleData,
  tableFieldMappings,
}) => {
  // Get sample data for selected table
  const tableSample = tableId && sampleData ? sampleData.table_samples[tableId] : null
  const tableMapping = tableFieldMappings.find((t) => t.tableId === tableId)

  // Build columns from header values
  const columns: ColumnsType<Record<string, any>> = useMemo(() => {
    if (!tableSample) return []

    return tableSample.header_values.map((header, idx) => ({
      title: header,
      dataIndex: `col_${idx}`,
      key: `col_${idx}`,
      width: 150,
      ellipsis: true,
    }))
  }, [tableSample])


  // Build data source from sample rows
  const dataSource = useMemo(() => {
    if (!tableSample) return []

    return tableSample.sample_data_rows.map((row, rowIdx) => {
      const rowData: Record<string, any> = { key: rowIdx }
      row.forEach((cell, colIdx) => {
        rowData[`col_${colIdx}`] = cell
      })
      return rowData
    })
  }, [tableSample])

  if (!tableId || !sampleData || !tableSample) {
    return null
  }

  return (
    <Modal
      title={`Sample Data: ${tableMapping?.tabName || tableId}`}
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
      ]}
      width={900}
    >
      <Table
        columns={columns}
        dataSource={dataSource}
        pagination={false}
        size="small"
        scroll={{ x: 'max-content' }}
      />
    </Modal>
  )
}

export default SampleDataModal
